import React, { useCallback, useEffect, useRef } from 'react';
import { Image, Platform, StyleSheet, View, type ImageStyle, type StyleProp } from 'react-native';

/**
 * ZoomableImage — pinch / pan / double-tap / wheel zoom for a single
 * image, on the WEB (which is the deployed target — app.remembr.xyz).
 *
 * Why this exists (#168): the previous full-screen viewer relied on
 * `<ScrollView maximumZoomScale minimumZoomScale>`. Those props are
 * **iOS-native-only** — on React Native Web they're inert, so pinch /
 * pan did nothing on the actual web app even though the UI advertised
 * "Pinch to zoom." This component implements real gestures with the DOM
 * Pointer Events API (no react-native-gesture-handler / reanimated
 * dependency — the project deliberately avoids those for a single
 * image surface).
 *
 * Gestures (web):
 *   - 2-finger pinch  → zoom toward the pinch midpoint (focal zoom)
 *   - 1-finger drag   → pan (only while zoomed in; at scale 1 it's locked)
 *   - double-tap/click→ toggle between fit (1×) and 2.5× at the tap point
 *   - wheel / trackpad→ zoom toward the cursor
 *
 * Transform is written directly to the <img> via ref during a gesture
 * (no React re-render per move event — smooth at 60fps). Scale is
 * clamped [1, MAX]; translate is clamped so the image can't be dragged
 * off-screen, and is forced to centered (0,0) at scale 1.
 *
 * Native (iOS/Android) isn't the shipping target today; there we fall
 * back to a plain contain-fit <Image> (the host modal still scrolls).
 */

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 280;

interface ZoomableImageProps {
  uri: string;
  onLoad?: () => void;
  onError?: () => void;
  style?: StyleProp<ImageStyle>;
  /** Bumping this (e.g. the photo URL/index) resets zoom to fit. */
  resetKey?: string | number;
  testID?: string;
}

const ImgTag = 'img' as unknown as React.ComponentType<any>;

export const ZoomableImage: React.FC<ZoomableImageProps> = ({
  uri,
  onLoad,
  onError,
  style,
  resetKey,
  testID,
}) => {
  // ── Native fallback (not the deployed path) ──────────────────────
  if (Platform.OS !== 'web') {
    return (
      <Image
        source={{ uri }}
        style={[styles.nativeImg, style]}
        resizeMode="contain"
        onLoad={onLoad}
        onError={onError}
        testID={testID}
      />
    );
  }

  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  // Live transform (mutated directly during gestures, no re-render).
  const tx = useRef({ scale: 1, x: 0, y: 0 });
  // Active pointers for pinch/pan bookkeeping.
  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinch = useRef<{ dist: number; midX: number; midY: number } | null>(
    null,
  );
  const lastTapAt = useRef(0);

  const apply = useCallback(() => {
    const el = imgRef.current;
    if (!el) return;
    const { scale, x, y } = tx.current;
    el.style.transform = `translate(${x}px, ${y}px) scale(${scale})`;
  }, []);

  const clampTranslate = useCallback((x: number, y: number, scale: number) => {
    const c = containerRef.current;
    if (!c) return { x, y };
    // Allowed travel each axis = half the overflow created by scaling.
    // At scale 1 this is 0 → the image stays centered (pan locked).
    const maxX = ((scale - 1) * c.clientWidth) / 2;
    const maxY = ((scale - 1) * c.clientHeight) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, []);

  /** Zoom toward a focal point (px relative to the container center). */
  const zoomTo = useCallback(
    (nextScaleRaw: number, focalX: number, focalY: number) => {
      const prev = tx.current;
      const nextScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nextScaleRaw));
      // Keep the focal point stationary on screen as scale changes.
      const k = nextScale / prev.scale;
      const nx = focalX - (focalX - prev.x) * k;
      const ny = focalY - (focalY - prev.y) * k;
      const clamped = clampTranslate(nx, ny, nextScale);
      tx.current = { scale: nextScale, x: clamped.x, y: clamped.y };
      apply();
    },
    [apply, clampTranslate],
  );

  const reset = useCallback(() => {
    tx.current = { scale: 1, x: 0, y: 0 };
    apply();
  }, [apply]);

  // Reset to fit whenever the source/resetKey changes.
  useEffect(() => {
    reset();
  }, [uri, resetKey, reset]);

  // Focal coords are measured relative to the container CENTER (matches
  // transform-origin: center) so the zoom math stays symmetric.
  const toFocal = useCallback((clientX: number, clientY: number) => {
    const c = containerRef.current;
    if (!c) return { fx: 0, fy: 0 };
    const r = c.getBoundingClientRect();
    return {
      fx: clientX - r.left - r.width / 2,
      fy: clientY - r.top - r.height / 2,
    };
  }, []);

  // ── Pointer + wheel wiring (native DOM listeners) ────────────────
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;

    const onPointerDown = (e: PointerEvent) => {
      c.setPointerCapture?.(e.pointerId);
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size === 2) {
        const pts = [...pointers.current.values()];
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        pinch.current = {
          dist: Math.hypot(dx, dy) || 1,
          midX: (pts[0].x + pts[1].x) / 2,
          midY: (pts[0].y + pts[1].y) / 2,
        };
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointers.current.has(e.pointerId)) return;
      const prevPt = pointers.current.get(e.pointerId)!;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.current.size >= 2 && pinch.current) {
        // Pinch-zoom toward the midpoint.
        const pts = [...pointers.current.values()];
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        const dist = Math.hypot(dx, dy) || 1;
        const ratio = dist / pinch.current.dist;
        const midClientX = (pts[0].x + pts[1].x) / 2;
        const midClientY = (pts[0].y + pts[1].y) / 2;
        const { fx, fy } = toFocal(midClientX, midClientY);
        zoomTo(tx.current.scale * ratio, fx, fy);
        pinch.current.dist = dist;
        e.preventDefault();
        return;
      }

      // Single-pointer drag → pan (only meaningful when zoomed in).
      if (pointers.current.size === 1 && tx.current.scale > 1) {
        const ddx = e.clientX - prevPt.x;
        const ddy = e.clientY - prevPt.y;
        const clamped = clampTranslate(
          tx.current.x + ddx,
          tx.current.y + ddy,
          tx.current.scale,
        );
        tx.current = { ...tx.current, x: clamped.x, y: clamped.y };
        apply();
        e.preventDefault();
      }
    };

    const endPointer = (e: PointerEvent) => {
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) pinch.current = null;
    };

    const onWheel = (e: WheelEvent) => {
      // Trackpad pinch surfaces as ctrlKey+wheel; plain wheel also zooms
      // here since this is a dedicated image viewer.
      e.preventDefault();
      const { fx, fy } = toFocal(e.clientX, e.clientY);
      const factor = Math.exp(-e.deltaY * 0.0015);
      zoomTo(tx.current.scale * factor, fx, fy);
    };

    const onDoubleClick = (e: MouseEvent) => {
      const { fx, fy } = toFocal(e.clientX, e.clientY);
      if (tx.current.scale > 1.01) reset();
      else zoomTo(DOUBLE_TAP_SCALE, fx, fy);
    };

    // Touch double-tap (Pointer Events don't emit dblclick on mobile).
    const onPointerUpTap = (e: PointerEvent) => {
      const now = Date.now();
      if (now - lastTapAt.current < DOUBLE_TAP_MS) {
        const { fx, fy } = toFocal(e.clientX, e.clientY);
        if (tx.current.scale > 1.01) reset();
        else zoomTo(DOUBLE_TAP_SCALE, fx, fy);
        lastTapAt.current = 0;
      } else {
        lastTapAt.current = now;
      }
    };

    c.addEventListener('pointerdown', onPointerDown);
    c.addEventListener('pointermove', onPointerMove);
    c.addEventListener('pointerup', endPointer);
    c.addEventListener('pointercancel', endPointer);
    c.addEventListener('pointerup', onPointerUpTap);
    c.addEventListener('wheel', onWheel, { passive: false });
    c.addEventListener('dblclick', onDoubleClick);

    return () => {
      c.removeEventListener('pointerdown', onPointerDown);
      c.removeEventListener('pointermove', onPointerMove);
      c.removeEventListener('pointerup', endPointer);
      c.removeEventListener('pointercancel', endPointer);
      c.removeEventListener('pointerup', onPointerUpTap);
      c.removeEventListener('wheel', onWheel);
      c.removeEventListener('dblclick', onDoubleClick);
    };
  }, [apply, clampTranslate, reset, toFocal, zoomTo]);

  return (
    <View style={[styles.webContainer, style]} testID={testID}>
      {/* Raw div+img so we get DOM Pointer Events + CSS transform.
          touchAction:'none' stops the browser from hijacking the pinch
          for page-zoom; cursor hints draggability when zoomed. */}
      {React.createElement(
        'div' as any,
        {
          ref: containerRef,
          style: {
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            cursor: 'grab',
          } as any,
        },
        <ImgTag
          ref={imgRef}
          src={uri}
          onLoad={onLoad}
          onError={onError}
          draggable={false}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            transformOrigin: 'center center',
            // No CSS transition — the transform updates per gesture frame.
            userSelect: 'none',
            willChange: 'transform',
          }}
        />,
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  webContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nativeImg: {
    width: '100%',
    height: '100%',
  },
});

export default ZoomableImage;
